import { useEffect, useRef, useState } from "react";

/**
 * True once `ref`'s element has actually entered the viewport (rootMargin: 0 — no preload
 * buffer, per product decision: strict on-screen only, skeleton covers the gap). Sticky: once
 * seen, stays true and the observer disconnects, so a playlist already loaded never re-queries
 * just because it scrolled back off-screen.
 */
export function useOnScreen<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [onScreen, setOnScreen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || onScreen) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setOnScreen(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [onScreen]);

  return [ref, onScreen];
}
